import { useMsal } from '@azure/msal-react';
import { Grid, Typography } from '@material-ui/core';
import React, { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';

import { Brand } from '@Praia-Health/spi-common-utils/types';
import { AmplitudeEventType } from '../../../../../analytics/amplitudeEvents';
import { BrandProps, ClientConfig } from '../../../../../clientConfig';
import { LocationState } from '../../../../../types/reactTypes';
import { ClientConfigContext } from '../../../../clientConfigContext';
import { buildIdVerificationEventProps, sendEvent } from '../../../../services/amplitudeClient';
import { sendVerificationCode } from '../../../../services/pims/pimsClient';
import getAccessToken from '../../../../util/getAccessToken';
import BrandedCard from '../../../containers/BrandedCard/BrandedCard';
import TwoButtonContainer from '../../../containers/TwoButtonContainer';
import MobilePhoneControl from '../../../controls/MobilePhoneControl';
import withLogger, { ComponentLoggerProps } from '../../../HOC/WithLogger';
import { AuthenticatedRoutes } from '../../../routers';
import { GeneralFormProps } from '../../types';

export type CollectPhoneNumberFormProps = {
    brandInfo: Brand;
    header: string;
};

function CollectPhoneNumberForm({ brandInfo, header, log }: CollectPhoneNumberFormProps & ComponentLoggerProps) {
    const { instance, accounts } = useMsal();
    const clientConfiguration = useContext<ClientConfig>(ClientConfigContext);

    const history = useHistory();
    const { handleSubmit, errors, control, formState } = useForm({ mode: 'onChange' });
    const [jwt, setJwt] = useState('');
    const location = useLocation<LocationState>();
    const { t, i18n } = useTranslation();

    useEffect(() => {
        getAccessToken(instance, accounts, clientConfiguration.b2cTenantConfig.scopes, log).then((jwt) => {
            setJwt(jwt);
            sendEvent(
                {
                    event_type: AmplitudeEventType.IdentityVerification_PhoneEntry,
                    event_properties: {
                        identity_verification: 'sms',
                        title_text: header,
                    },
                },
                jwt,
            );
        });
    }, []);
    const onSubmit = async (data: any) => {
        const eventProps = buildIdVerificationEventProps(AmplitudeEventType.IdentityVerification_PhoneEntry_Request, 'sms');
        sendEvent(eventProps, jwt);
        const numberNoDashes = data.cellPhoneNumber.replace(/[-]/g, '');
        try {
            const response = await sendVerificationCode({
                jwt,
                value: numberNoDashes,
                pimsApiUrl: clientConfiguration.pimsApiUrl,
                language: i18n.languages[0],
            });
            if (response.ok) {
                history.push({
                    pathname: '/verification-code',
                    state: { phoneNumber: data.cellPhoneNumber, isCrossRegion: location?.state?.isCrossRegion },
                });
            } else {
                if (clientConfiguration.bypassProve) {
                    history.push({
                        pathname: '/verification-code',
                        state: { phoneNumber: data.cellPhoneNumber, isCrossRegion: location?.state?.isCrossRegion },
                    });
                } else {
                    // TODO: Better alternative, stay on this page and allow user to re-send the code?
                    log.error(`Sent verification code fail. Error occurred - ${response.status}${response.statusText ? ` -  ${response.statusText}` : ''}`);
                    history.push('/error');
                }
            }
        } catch (err: any) {
            log.error(`Error occurred - ${err.message}`);
            history.push('/error');
        }
    };

    const handleCancelClick = () => {
        history.push({
            pathname: AuthenticatedRoutes.VerificationRoutes.VerificationMenu,
            state: {
                isCrossRegion: location?.state?.isCrossRegion,
                showVerificationMethods: true,
            },
        });
    };

    const { isDirty, isValid } = formState;

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
                <CollectPhoneNumberCard
                    brandInfo={brandInfo}
                    control={control}
                    errors={errors}
                    header={header}
                    t={t}
                    isDirty={isDirty}
                    isValid={isValid}
                    onCancel={handleCancelClick}
                />
            </form>
        </>
    );
}

export function CollectPhoneNumberCard({
    brandInfo,
    header,
    control,
    errors,
    t,
    isDirty,
    isValid,
    onCancel,
}: BrandProps &
    GeneralFormProps & {
        header: string;
        t: any;
        isDirty: boolean;
        isValid: boolean;
        onCancel: () => void;
    }): JSX.Element {
    return (
        <BrandedCard brand={brandInfo}>
            {{
                header: (
                    <>
                        <Typography variant='h2'>{t(header)}</Typography>
                    </>
                ),
                content: (
                    <>
                        <Grid container justify='center' item xs={12}>
                            <Grid item xs={12}>
                                <Typography variant='h3'>{t('To verify your identity, we will send you an SMS text message with a code to your cell phone.')}</Typography>
                            </Grid>
                            <Grid item xs={12}>
                                &nbsp;
                            </Grid>
                            <Grid item xs={12}>
                                <MobilePhoneControl control={control} errors={errors} labelOverride={'Cell phone number'} />
                            </Grid>
                        </Grid>
                    </>
                ),
                actions: (
                    <Grid container justify='space-around' item xs={12}>
                        <TwoButtonContainer
                            firstButtonProps={{
                                variant: 'text',
                                label: t('Cancel'),
                                brand: brandInfo.brand,
                                letterSpacing: 0,
                                lineHeight: '16px',
                                textAlign: 'center',
                                onClick: onCancel,
                            }}
                            secondButtonProps={{
                                disabled: !isDirty || !isValid,
                                variant: 'contained',
                                label: t('Request Code'),
                                brand: brandInfo.brand,
                                type: 'submit',
                                noWrap: true,
                            }}
                        />
                    </Grid>
                ),
            }}
        </BrandedCard>
    );
}

export default withLogger(CollectPhoneNumberForm, { eventSource: 'CollectPhoneNumberForm' });
